import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { useForm, ValidationError } from '@formspree/react';
import {
  ChevronRight,
  ChevronDown,
  ArrowUpRight,
  ArrowUp,
  Check,
  Users,
  TrendingUp,
  Target,
  Award,
  DotIcon,
  ShieldCheck,
  Briefcase,
  FileText,
  Handshake,
  Radar,
  Building2,
  BadgeCheck,
  Sparkles,
} from 'lucide-react';

import * as assets from '../../assets';

/* =========================================================
   CONTENT (Upgraded for credibility + "team" positioning)
========================================================= */

const PRINCIPLES = [
  {
    title: 'Principal-led accountability',
    text: 'Every engagement is led by a senior advisor with transaction experience — accountable for strategy, execution quality, and outcomes end-to-end.',
  },
  {
    title: 'Commercial realism',
    text: 'Advice grounded in buyer psychology, market evidence, and transaction mechanics — not templates, theory, or generic broker playbooks.',
  },
  {
    title: 'Outcome alignment',
    text: 'We structure incentives around completion, value and certainty — so you’re not paying for activity, you’re paying for progress.',
  },
];

const TRUST_SIGNALS = [
  {
    icon: ShieldCheck,
    title: 'Confidential by design',
    text: 'Strict confidentiality, staged outreach, and controlled disclosure. We protect leverage and minimise business disruption.',
  },
  {
    icon: FileText,
    title: 'Buyer-ready materials',
    text: 'Clear financial narrative, defensible valuation logic, and a buyer-ready information pack that withstands diligence scrutiny.',
  },
  {
    icon: Radar,
    title: 'Buyer strategy, not “spray and pray”',
    text: 'Targeted buyer mapping and outreach to decision-makers. We optimise for fit, price tension, and completion probability.',
  },
  {
    icon: Handshake,
    title: 'Negotiation discipline',
    text: 'We control the process, set timelines, and maintain momentum — reducing deal fatigue and protecting value.',
  },
];

const TEAM_BLOCKS = [
  {
    icon: Briefcase,
    title: 'Corporate advisory team',
    text: 'Senior-led transaction support with structured workstreams across valuation, materials, buyer outreach, diligence coordination, and negotiation support.',
  },
  {
    icon: Building2,
    title: 'Research & buyer mapping',
    text: 'Dedicated capability for buyer identification, thesis-driven targeting, and outreach planning — helping build competitive tension and deal certainty.',
  },
  {
    icon: BadgeCheck,
    title: 'Financial modelling & diligence support',
    text: 'CFO-grade modelling, quality of earnings lens, and diligence readiness so your numbers hold up and your story stays coherent under pressure.',
  },
];

const INDUSTRY_GROUPS = [
  {
    key: 'health',
    title: 'Healthcare & allied health',
    buyerLens: 'Recurring revenue, practitioner dependence, regulatory risk',
    points: [
      'GP, dental, allied health & specialist practices',
      'Patient churn, referral dynamics, and contract structures',
      'Founder / clinician dependency mitigation',
      'Private equity and consolidator buyer activity',
    ],
  },

  {
    key: 'services',
    title: 'Professional & business services',
    buyerLens: 'People risk, client concentration, transferability',
    points: [
      'Accounting, advisory, legal-adjacent and consulting firms',
      'Fee mix, utilisation, and margin sustainability',
      'Client retention post-founder exit',
      'Partner buy-in and succession structuring',
    ],
  },

  {
    key: 'financial',
    title: 'Financial services',
    buyerLens: 'Regulatory risk, client stickiness, compliance maturity',
    points: [
      'Mortgage broking, wealth advice, insurance and financial intermediaries',
      'Recurring vs transactional revenue quality',
      'Regulatory history, licensing, and compliance controls',
      'Buyer scrutiny on clawbacks, churn and adviser dependency',
    ],
  },

  {
    key: 'realestate',
    title: 'Real estate & property services',
    buyerLens: 'Listing quality, agent dependency, market cyclicality',
    points: [
      'Residential, commercial and mixed real estate agencies',
      'Sales vs annuity income mix (management, rent rolls, recurring fees)',
      'Agent concentration and retention risk',
      'Sensitivity to interest rates, volumes and local market cycles',
    ],
  },

  {
    key: 'tech',
    title: 'Technology & SaaS',
    buyerLens: 'ARR quality, churn, scalability',
    points: [
      'B2B SaaS, vertical software, and tech-enabled services',
      'Revenue visibility, cohort behaviour, and retention',
      'Product dependency and roadmap credibility',
      'Strategic vs financial buyer positioning',
    ],
  },

  {
    key: 'ecommerce',
    title: 'E-commerce & digital-first brands',
    buyerLens: 'Unit economics, traffic risk, repeat purchase behaviour',
    points: [
      'Direct-to-consumer and marketplace-led businesses',
      'Customer acquisition efficiency and payback periods',
      'Channel concentration (Meta, Google, Amazon)',
      'Brand durability versus founder-led marketing',
    ],
  },

  {
    key: 'industrial',
    title: 'Trades, industrial & construction services',
    buyerLens: 'Contract visibility, systems, safety & compliance',
    points: [
      'Trade services, project-based operators, maintenance contracts',
      'Backlog quality and earnings normalisation',
      'WHS, licensing, and compliance exposure',
      'Transition from founder-led operations',
    ],
  },

  {
    key: 'consumer',
    title: 'Consumer, retail & hospitality',
    buyerLens: 'Margin resilience, brand strength, cyclicality',
    points: [
      'Retail, hospitality, accommodation and multi-site operators',
      'Labour intensity and cost inflation sensitivity',
      'Site-level profitability and scalability',
      'Seasonality and earnings defensibility',
    ],
  },
];



const SERVICES = [
  {
    title: 'Selling a business',
    description:
      'Principal-led sale process from preparation through to settlement — optimised for maximum value, discretion, and completion certainty.',
    featured: true,
  },
  {
    title: 'Business valuations',
    description:
      'Independent, defensible valuations grounded in transaction evidence, market multiples, and cash-flow fundamentals.',
  },
  {
    title: 'Business sales & acquisitions',
    description:
      'Seller-side transaction support and acquisition process advisory — including preparation, buyer identification, materials, and commercial negotiation support.',
  },
  {
    title: 'Capital strategy (planning & scenario modelling)',
    description:
      'Strategic capital planning, scenario analysis, and preparation for discussions with licensed advisers — without providing financial product advice.',
  },
  {
    title: 'Ownership transitions & succession',
    description:
      'Commercial planning and transaction pathway support for exits, internal transfers, and succession — delivered alongside legal and tax advisers.',
  },
  {
    title: 'Transaction readiness & CFO-level modelling',
    description:
      'Buyer-reaqdy materials, financial preparation, and diligence readiness that accelerates timelines and strengthens valuation outcomes.',
  },
];

const ADVANTAGES = [
  {
    icon: Users,
    title: 'Direct access to senior advisers',
    description:
      'Work with experienced principals and a delivery team built for execution. No disappearing act after the pitch — senior involvement remains through to settlement.',
    stat: 'Senior',
    statLabel: 'Led',
  },
  {
    icon: TrendingUp,
    title: 'Real transaction experience',
    description:
      'We understand buyers because we’ve worked in CFO, board, and transaction environments. We know what creates confidence — and what creates discounts.',
    stat: 'CFO',
    statLabel: 'Level lens',
  },
  {
    icon: Target,
    title: 'Outcome-focused process',
    description:
      'We manage the process to keep momentum and protect value: controlled information flow, structured timelines, and negotiation discipline.',
    stat: 'Deal',
    statLabel: 'Momentum',
  },
  {
    icon: Award,
    title: 'Strategic buyer network & targeting',
    description:
      'Targeted access to strategic acquirers and private capital-backed buyers — built through relationships, research, and outreach that finds the true decision-makers.',
    stat: 'Targeted',
    statLabel: 'Buyers',
  },
];

const WHO_WE_HELP = [
  'Founder-led or closely held businesses',
  'Business owners and shareholders considering exits, succession, or strategic change',
  'Boards seeking senior, independent transaction judgement',
];

const WHAT_BUYERS_CARE = [
  {
    title: 'Quality of earnings',
    text: 'Sustainable earnings, clean add-backs, and repeatable drivers — not one-off wins or messy adjustments.',
  },
  {
    title: 'Customer concentration & retention',
    text: 'Churn, contract terms, referral dependence, and whether revenue is resilient without the founder.',
  },
  {
    title: 'Operational scalability',
    text: 'Systems, processes, and people depth — buyers pay premiums for businesses that don’t rely on key-person heroics.',
  },
  {
    title: 'Risk & compliance',
    text: 'Regulatory exposure, contracts, employment, WHS, and IP. The goal is minimising “unknown unknowns.”',
  },
];

const STATS = [
  ['$2.5M+', 'Australian SMEs', 'Active across all sectors'],
  ['$4T+', 'Estimated SME enterprise value', 'Private Australian businesses'],
  ['70%', 'Founder-owned', 'Succession & exit decisions ahead'],
  ['Mid-market', 'Execution gap', 'Between brokers and banks'],
];

const advantageItem = {
  hidden: { opacity: 0, y: 28 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.08,
      duration: 0.55,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

const advantageHover = {
  hover: {
    y: -4,
    transition: { duration: 0.25, ease: 'easeOut' },
  },
};

/**
 * Compact process: one-screen friendly.
 * Left: timeline “steps”
 * Right: details for selected step
 */
const PROCESS_STEPS = [
  {
    key: 'engage',
    week: 'Step 1',
    duration: 'Days 1–5',
    title: 'Engagement & deal planning',
    text:
      'We align on objectives, timeline, confidentiality, and the commercial reality of your options. We establish the strategy, workplan, and success metrics.',
    items: [
      'Confidential engagement & scope',
      'Readiness review + risk scan',
      'Deal strategy and positioning',
      'Workplan, responsibilities, and timeline',
    ],
  },
  {
    key: 'value',
    week: 'Step 2',
    duration: 'Week 1–2',
    title: 'Valuation & value driver plan',
    text:
      'We build a defensible valuation view and identify the levers buyers will pay for — turning “a good business” into a buyer-ready proposition.',
    items: [
      'Valuation range with supporting evidence',
      'Value drivers and optimisation plan',
      'Buyer thesis: who pays the premium and why',
      'Commercial structure considerations (legal and tax led)',
    ],
  },
  {
    key: 'buyers',
    week: 'Step 3',
    duration: 'Week 2–3',
    title: 'Buyer identification & outreach',
    text:
      'We map strategic acquirers and private capital-backed buyers, prioritise decision-makers, and run a controlled outreach process designed to build tension and protect confidentiality.',
    items: [
      'Target list (strategic acquirers and private capital-backed buyers)',
      'Teaser and NDA workflow',
      'Outreach sequencing and tracking',
      'Management of inbound interest',
    ],
  },
  {
    key: 'im',
    week: 'Step 4',
    duration: 'Week 3–5',
    title: 'Information memorandum & data room',
    text:
      'We prepare buyer-ready materials that tell your story clearly and credibly — with numbers that reconcile and a narrative that survives diligence.',
    items: [
      'Information memorandum (IM)',
      'Financial model + projections',
      'Data room structure and checklist',
      'Q&A preparation and messaging',
    ],
  },
  {
    key: 'offers',
    week: 'Step 5',
    duration: 'Week 5–7',
    title: 'Offers, shortlist & negotiation',
    text:
      'We compare offers, assess buyer capability, and support negotiation of indicative commercial terms to optimise price and completion certainty.',
    items: [
      'Offer comparison and scoring',
      'Buyer diligence on buyers (capability)',
      'Shortlist strategy + timelines',
      'Indicative terms negotiation',
    ],
  },
  {
    key: 'dd',
    week: 'Step 6',
    duration: 'Week 7–10',
    title: 'Due diligence management',
    text:
      'We coordinate diligence, control information flow, and maintain momentum — reducing disruptions while protecting your leverage.',
    items: [
      'Diligence tracker and coordination',
      'Response strategy + narrative consistency',
      'Commercial risk mitigation',
      'Stakeholder and advisor coordination',
    ],
  },
  {
    key: 'close',
    week: 'Step 7',
    duration: 'Week 10–12',
    title: 'Binding terms & completion',
    text:
      'We support final negotiations and work alongside legal counsel to close efficiently — resolving issues while protecting value and certainty.',
    items: [
      'Commercial terms input',
      'Deal structure and completion planning',
      'Settlement coordination support',
      'Post-deal transition considerations',
    ],
  },
];

const ITEMS = [
  {
    title: 'Initial consultation',
    text:
      'A confidential discussion to understand your objectives, timeline and constraints. No pressure — just an honest assessment of pathways and readiness.',
  },
  {
    title: 'Transparent engagement',
    text:
      'Clear scope, defined deliverables, and a process designed around outcomes. You’ll know what happens next, who does what, and how progress is measured.',
  },
  {
    title: 'Ongoing partnership',
    text:
      'We stay accountable through the full journey — not a handoff to juniors. You have a consistent senior point of contact plus delivery support.',
  },
];

/* =========================================================
   MOTION
========================================================= */

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};

const cardVariantsOther = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  },
};

const cardVariants = {
  rest: { y: 0 },
  hover: { y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const arrowVariants = {
  rest: { x: 0, y: 0 },
  hover: { x: 6, y: -6 },
};

const FadeUp = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-120px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
};

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};



/* =========================================================
   PAGE
========================================================= */

const HomePage = () => {
  const { scrollY } = useScroll();
  const heroOpacity = useTransform(scrollY, [0, 400], [1, 0]);
  const scrollOpacity = useTransform(scrollY, [0, 400], [0, 1]);
  const heroY = useTransform(scrollY, [0, 400], [0, -120]);
  const showTop = useTransform(scrollY, [300, 500], [0, 1]);

  const [selectedIndustry, setSelectedIndustry] = useState(INDUSTRY_GROUPS[0].key);


  const currentYear = new Date().getFullYear();

  const [state, handleSubmit] = useForm("xojjarqy");

  const [submittedData, setSubmittedData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
  });


  // Navbar theme switching based on sections in view
  const [navTone, setNavTone] = useState('light'); // 'light' | 'dark'

  const darkSectionRefs = useRef([]);
  darkSectionRefs.current = [];

  const registerDarkSection = (el) => {
    if (el && !darkSectionRefs.current.includes(el)) {
      darkSectionRefs.current.push(el);
    }
  };

  useEffect(() => {
    const els = darkSectionRefs.current.filter(Boolean);
    if (!els.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // If any dark section is significantly visible, switch nav to dark tone
        const anyDarkVisible = entries.some((e) => e.isIntersecting && e.intersectionRatio > 0.2);
        setNavTone(anyDarkVisible ? 'dark' : 'light');
      },
      {
        root: null,
        threshold: [0.2, 0.35, 0.5],
      }
    );

    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Compact process selection
  const [selectedStepKey, setSelectedStepKey] = useState(PROCESS_STEPS[0].key);
  const selectedStep = useMemo(
    () => PROCESS_STEPS.find((s) => s.key === selectedStepKey) || PROCESS_STEPS[0],
    [selectedStepKey]
  );


const onSubmit = (e) => {
  e.preventDefault(); // CRITICAL

  if (window.gtag) {
    window.gtag_report_conversion();
  }

  handleSubmit(e);
};


  return (
    <Page>
      {/* ===================================================
         HERO
      =================================================== */}
      <Hero id="top">
        <Nav $tone={navTone}>
          <NavInner>
            <BrandRow onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              { navTone !== 'dark' ? <Logo src={assets.LogoFull} alt="SME Cap Advisory" /> : <Logo src={assets.LogoWhite} alt="SME Cap Advisory" /> }
            </BrandRow>

            <NavLinks>
              <NavLink $tone={navTone} onClick={() => scrollToId('services')}>Services</NavLink>
              <NavLink $tone={navTone} onClick={() => scrollToId('advantage')}>Advantage</NavLink>
              <NavLink $tone={navTone} onClick={() => scrollToId('process')}>Process</NavLink>
              <NavLink $tone={navTone} onClick={() => scrollToId('team')}>Team</NavLink>
              <NavLink $tone={navTone} onClick={() => scrollToId('contact')}>Contact</NavLink>
            </NavLinks>

            <NavCTA $tone={navTone} onClick={() => scrollToId('contact')}>
              Start a conversation <ArrowUpRight size={16} />
            </NavCTA>
          </NavInner>
        </Nav>

        <HeroVideo autoPlay muted loop playsInline preload="auto" poster="/hero-fallback.jpg">
          <source src={assets.Video} type="video/mp4" />
        </HeroVideo>

        <HeroOverlay />

        <HeroInner as={motion.div} style={{ opacity: heroOpacity, y: heroY }}>
          <HeroKicker>
            <Sparkles size={16} />
            Boutique corporate advisory • Principal-led • Built for SME founders
          </HeroKicker>

          <HeroTitle>
            Corporate advisory for <em>SME founders</em>, owners and boards
          </HeroTitle>

          <HeroSub>
             Experts in business sales, valuations, and transaction preparation
          </HeroSub>

          <HeroActions>
            <PrimaryCTA onClick={() => scrollToId('contact')}>
              Start a confidential discussion
              <motion.span
                style={{ position: 'relative', top: 2 }}
                animate={{ x: [0, 6, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              >
                <ChevronRight />
              </motion.span>
            </PrimaryCTA>

            <SecondaryCTA onClick={() => scrollToId('services')}>
              Explore our services
            </SecondaryCTA>
          </HeroActions>

          <PillAlign>
            <Pill><PillDot /> Principal-led engagements</Pill>
            <Pill><PillDot /> $1m–$100m transaction focus</Pill>
            <Pill><PillDot /> Success-aligned fee options</Pill>
          </PillAlign>

        </HeroInner>

        <ScrollCue>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            style={{ opacity: scrollOpacity }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <ScrollCueInner>
              Scroll to explore
              <ChevronDown />
            </ScrollCueInner>
          </motion.div>
        </ScrollCue>
      </Hero>

      {/* ===================================================
         ABOUT
      =================================================== */}

    


      <AltSection>
        <InnerSection>
          <FadeUp>
            <Eyebrow>Why SME Cap Advisory exists</Eyebrow>

            <SectionTitle>Senior-led corporate advisory for SMEs</SectionTitle>

            <Body>
              SME Cap Advisory is a boutique corporate advisory firm built to close the execution gap
              between business brokers and investment banks — giving Australian business owners access to
              senior, commercially grounded transaction expertise.
              <br /><br />
              We’re structured like a deal team: principal-led strategy and negotiation, supported by
              disciplined delivery across valuation, materials, buyer mapping, diligence coordination and
              process control — so owners can keep running the business while the transaction progresses.
            </Body>

            <Grid>
              {PRINCIPLES.map((p, i) => (
                <CardNext key={p.title}
                  as={motion.div}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.12, duration: 0.5 }}
                  viewport={{ once: true }}
                >
                  <CardInner>
                    <CardTitle>{p.title}</CardTitle>
                    <CardDescription>{p.text}</CardDescription>
                  </CardInner>
                </CardNext>
              ))}
            </Grid>



          </FadeUp>
        </InnerSection>
      </AltSection>


     
      

      {/* ===================================================
         STATS
      =================================================== */}
      <Section>
        <InnerSection>
          <FadeUp>
            <SectionTitle>A market opportunity built on fundamentals</SectionTitle>
            <SectionSub>
              Australia’s SME market holds enormous untapped value — but outcomes depend on preparation,
              process discipline, and credible execution. These fundamentals drive why senior-led advisory
              matters.
            </SectionSub>

            <StatsGrid>
              {STATS.map(([value, label, sublabel], i) => (
                <Stat
                  as={motion.div}
                  key={label}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.15 }}
                  viewport={{ once: true }}
                >
                  {value === 'Mid-market' ? (
                    <StatValueSmaller
                      as={motion.div}
                      key={label}
                      initial={{ scale: 0.3 }}
                      whileInView={{ scale: 1 }}
                      transition={{ delay: i * 0.15 }}
                      viewport={{ once: true }}
                    >
                      {value}
                    </StatValueSmaller>
                  ) : (
                    <StatValue
                      as={motion.div}
                      key={label}
                      initial={{ scale: 0.3 }}
                      whileInView={{ scale: 1 }}
                      transition={{ delay: i * 0.15 }}
                      viewport={{ once: true }}
                    >
                      {value}
                    </StatValue>
                  )}

                  <StatLabel>{label}</StatLabel>
                  <StatLabelSmall>{sublabel}</StatLabelSmall>
                </Stat>
              ))}
            </StatsGrid>

            <ValueNarrative>
              <ValueNarrativeTitle>What this means for owners</ValueNarrativeTitle>
              <ValueNarrativeText>
                Many successful SMEs are undervalued because the story is unclear, the numbers aren’t packaged defensibly,
                and the process isn’t controlled. Our work focuses on turning real business quality into buyer confidence —
                and buyer confidence into price and certainty.
              </ValueNarrativeText>
              <ValueBullets>
                <li><Check size={18} /> Reduce key-person risk and improve buyer confidence</li>
                <li><Check size={18} /> Present clean earnings and credible, repeatable drivers</li>
                <li><Check size={18} /> Run a process that builds tension and protects leverage</li>
              </ValueBullets>
            </ValueNarrative>
          </FadeUp>
        </InnerSection>
      </Section>

      {/* ===================================================
         SERVICES
      =================================================== */}
      <AltSection id="services">
        <InnerSection>
          <Header>
            <SectionTitle>What we do</SectionTitle>
            <SectionSub>
              Hands-on corporate advisory services tailored to SME and mid-market transactions.
              Whether you’re considering a sale, recapitalisation, acquisition, or valuation — we deliver
              disciplined strategy and executable pathways.
            </SectionSub>
          </Header>

          <Grid>
            {SERVICES.map((service) => (
              <Card
                key={service.title}
                as={motion.div}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                viewport={{ once: true, margin: '-120px' }}
                whileHover="hover"
                animate="rest"
                variants={cardVariants}
                featured={service.featured}
              >
                <CardInner>
                  <CardHeader>
                    <CardTitle>{service.title}</CardTitle>

                    <ArrowWrap variants={arrowVariants} transition={{ duration: 0.25, ease: 'easeOut' }}>
                      <ArrowUpRight size={20} />
                    </ArrowWrap>
                  </CardHeader>

                  <CardDescription>{service.description}</CardDescription>
                </CardInner>
              </Card>
            ))}
          </Grid>

        </InnerSection>
      </AltSection>

    

      {/* ===================================================
         ADVANTAGE
      =================================================== */}
      <Section id="advantage">
        <InnerSection>
          <Header>
            <SectionTitle>The SME Cap Advisory advantage</SectionTitle>
            <SectionSub>
              We operate differently to traditional practices — built for SME owners who need senior judgement
              and real execution capability.
              <br /><br />
              <strong>Who we’re best suited to work with:</strong>
            </SectionSub>

            <Checklist>
              {WHO_WE_HELP.map((x, idx) => (
                <li key={idx}><DotIcon size={18} /> {x}</li>
              ))}
            </Checklist>
          </Header>

          <Cards
            as={motion.div}
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
          >
            {ADVANTAGES.map((item, i) => {
              const Icon = item.icon;
              return (
                <CardOther
                  key={i}
                  as={motion.div}
                  variants={advantageItem}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  whileHover="hover"
                  viewport={{ once: true, margin: '-120px' }}
                  {...advantageHover} 
                >
                  <Top>
                    <LeftAlign>
                      <IconWrap>
                        <Icon size={22} />
                      </IconWrap>
                      <CardTitle>{item.title}</CardTitle>
                    </LeftAlign>

                    <StatWrap>
                      <StatNew>{item.stat}</StatNew>
                      <StatLabelNew>{item.statLabel}</StatLabelNew>
                    </StatWrap>
                  </Top>

                  <CardDescription>{item.description}</CardDescription>
                </CardOther>
              );
            })}
          </Cards>

          
        </InnerSection>
      </Section>

      {/* ===================================================
         PROCESS (Compact, one-screen friendly)
      =================================================== */}
      <AltSection id="process">
        <InnerSection>
          <Header>
            <SectionTitle>Our proven transaction process</SectionTitle>
            <SectionSub>
              A structured seven-step roadmap designed to minimise risk, enhance buyer confidence, and maximise value —
              while maintaining business continuity.
            </SectionSub>
          </Header>

          <ProcessShell>
            <ProcessSidebar>
              <ProcessMeta>
                <ProcessMetaLabel>Typical transaction timeline</ProcessMetaLabel>
                <ProcessMetaValue>10–12 weeks</ProcessMetaValue>
                <ProcessMetaSub>From engagement to completion (varies by complexity and buyer)</ProcessMetaSub>
              </ProcessMeta>

              <ProcessSteps>
                {PROCESS_STEPS.map((s, idx) => {
                  const active = s.key === selectedStepKey;
                  return (
                    <ProcessStepButton
                      key={s.key}
                      $active={active}
                      onClick={() => setSelectedStepKey(s.key)}
                      type="button"
                    >
                      <ProcessStepLeft>
                        <ProcessStepIdx>{idx + 1}</ProcessStepIdx>
                        <div>
                          <ProcessStepTitle>{s.title}</ProcessStepTitle>
                          <ProcessStepMini>{s.duration}</ProcessStepMini>
                        </div>
                      </ProcessStepLeft>
                      <ChevronRight size={18} />
                    </ProcessStepButton>
                  );
                })}
              </ProcessSteps>

              <ProcessHint>
                Tip: Click a step to view deliverables. This keeps the process understandable without a long scroll.
              </ProcessHint>
            </ProcessSidebar>

            <ProcessDetail>
              <ProcessDetailTop>
                <ProcessDetailEyebrow>{selectedStep.week} • {selectedStep.duration}</ProcessDetailEyebrow>
                <ProcessDetailTitle>{selectedStep.title}</ProcessDetailTitle>
                <ProcessDetailText>{selectedStep.text}</ProcessDetailText>
              </ProcessDetailTop>

              <KeyLabel>Key deliverables:</KeyLabel>

              <Checklist>
                {selectedStep.items.map((item) => (
                  <li key={item}>
                    <Check size={18} />
                    {item}
                  </li>
                ))}
              </Checklist>

              <ProcessAssurance>
                <AssuranceItem>
                  <AssuranceIcon><ShieldCheck size={18} /></AssuranceIcon>
                  <div>
                    <AssuranceTitle>Confidentiality controls</AssuranceTitle>
                    <AssuranceText>We control disclosure and sequencing to protect leverage and reduce disruption.</AssuranceText>
                  </div>
                </AssuranceItem>
                <AssuranceItem>
                  <AssuranceIcon><Target size={18} /></AssuranceIcon>
                  <div>
                    <AssuranceTitle>Buyer-fit prioritisation</AssuranceTitle>
                    <AssuranceText>We focus on buyers most likely to pay for your value drivers — and close.</AssuranceText>
                  </div>
                </AssuranceItem>
                <AssuranceItem>
                  <AssuranceIcon><FileText size={18} /></AssuranceIcon>
                  <div>
                    <AssuranceTitle>Buyer-ready packaging</AssuranceTitle>
                    <AssuranceText>Numbers that reconcile. A narrative that holds under diligence. A process that builds confidence.</AssuranceText>
                  </div>
                </AssuranceItem>
              </ProcessAssurance>
            </ProcessDetail>
          </ProcessShell>
        </InnerSection>
      </AltSection>

       {/* ===================================================
        HOW BUYERS ASSESS VALUE
      =================================================== */}
      <Section>
         <InnerSection>
            <Eyebrow>Buyer perspective</Eyebrow>

            <SectionTitle>How buyers assess value</SectionTitle>

            <SectionSub>
              Buyers follow a predictable decision path.  
              Value is created — or lost — at each step.
            </SectionSub>

            <BuyerPath>
              {/* STEP 1 */}
              <BuyerStep
                as={motion.div}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <BuyerStepIndex>1</BuyerStepIndex>
                <BuyerStepContent>
                  <BuyerStepTitle>Frame the risk</BuyerStepTitle>
                  <BuyerStepText>
                    Buyers start by forming a risk-adjusted view of the business.
                    Price, structure, and certainty are driven less by headline
                    earnings and more by predictability, transferability, and
                    defensibility under diligence.
                  </BuyerStepText>
                </BuyerStepContent>
              </BuyerStep>

              {/* STEP 2 */}
              <BuyerStep
                as={motion.div}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <BuyerStepIndex>2</BuyerStepIndex>
                <BuyerStepContent>
                  <BuyerStepTitle>Test the earnings</BuyerStepTitle>
                  <BuyerCriteriaGrid>
                    <BuyerCriteria>
                      <strong>Quality of earnings</strong>
                      <span>Sustainable earnings, clean add-backs, repeatable drivers</span>
                    </BuyerCriteria>
                    <BuyerCriteria>
                      <strong>Customer resilience</strong>
                      <span>Concentration, churn, contracts, founder dependency</span>
                    </BuyerCriteria>
                    <BuyerCriteria>
                      <strong>Operational scalability</strong>
                      <span>Systems, people depth, and ability to scale without heroics</span>
                    </BuyerCriteria>
                    <BuyerCriteria>
                      <strong>Risk & compliance</strong>
                      <span>Contracts, regulation, employment, WHS, IP exposure</span>
                    </BuyerCriteria>
                  </BuyerCriteriaGrid>
                </BuyerStepContent>
              </BuyerStep>

              {/* STEP 3 */}
              <BuyerStep
                as={motion.div}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <BuyerStepIndex>3</BuyerStepIndex>
                <BuyerStepContent>
                  <BuyerStepTitle>Apply discounts or premiums</BuyerStepTitle>
                  <BuyerSplit>
                    <BuyerSplitCol>
                      <BuyerSplitTitle>Value leaks when</BuyerSplitTitle>
                      <ul>
                        <li>Numbers don’t reconcile</li>
                        <li>Knowledge sits with the founder</li>
                        <li>Documentation is informal</li>
                        <li>Diligence is reactive</li>
                      </ul>
                    </BuyerSplitCol>

                    <BuyerSplitCol>
                      <BuyerSplitTitle>Pricing power emerges when</BuyerSplitTitle>
                      <ul>
                        <li>Earnings are clean and defensible</li>
                        <li>Depth exists beyond key people</li>
                        <li>Systems and data are credible</li>
                        <li>Risk is proactively addressed</li>
                      </ul>
                    </BuyerSplitCol>
                  </BuyerSplit>
                </BuyerStepContent>
              </BuyerStep>

              {/* STEP 4 */}
              <BuyerStep
                as={motion.div}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <BuyerStepIndex>4</BuyerStepIndex>
                <BuyerStepContent>
                  <BuyerStepTitle>Decide on price, structure & certainty</BuyerStepTitle>
                  <BuyerStepText>
                    A controlled, competitive process converts confidence into
                    leverage — ensuring buyers compete on price and terms,
                    not perceived risk.
                  </BuyerStepText>

                  <BuyerStepCallout>
                    <strong>Our role:</strong> run a disciplined process that
                    protects leverage, sustains momentum, and defends value
                    through to completion.
                  </BuyerStepCallout>
                </BuyerStepContent>
              </BuyerStep>
            </BuyerPath>
        </InnerSection>
      </Section>


     {/* ===================================================
        STRATEGY → EXECUTION (Optiii)
      =================================================== */}
      <AltSection>
        <InnerSection>
          <Header>
            <Eyebrow>Finance readiness</Eyebrow>
            <SectionTitle>Buyer confidence starts before the sale process</SectionTitle>
            <SectionSub>
              Strong outcomes require more than strategy.
              Buyers pay premiums for businesses with clean numbers,
              disciplined reporting, and finance functions that hold up under diligence.
              <br /><br />
              Where finance readiness is a constraint, we partner with{' '}<strong>Optiii</strong>{' '}
              to prepare businesses for scrutiny — before a process begins.
            </SectionSub>
          </Header>

          {/* EXECUTION BRIDGE */}
          <ExecutionBridge>
            <ExecutionLeft>
              <ExecutionTitle>Why finance readiness matters</ExecutionTitle>
              <ExecutionText>
                Many SMEs are fundamentally strong — but undervalued —
                because the finance function isn’t buyer-ready.
                <br /><br />
                Inconsistent reporting, unclear add-backs, manual processes,
                or founder-dependent knowledge introduce risk —
                and risk is priced aggressively by buyers.
                <br /><br />
                Optiii prepares finance teams for transaction scrutiny,
                so value is defended before buyers ever open a data room.
              </ExecutionText>

              <ExecutionCallout>
                <strong>Simple truth:</strong><br />
                SME Cap Advisory defines the value story.<br />
                Optiii ensures the numbers can support it.
              </ExecutionCallout>
            </ExecutionLeft>

            <ExecutionRight>
              <ExecutionCard>
                <ExecutionCardTitle>Finance automation & reporting</ExecutionCardTitle>
                <ExecutionCardText>
                  CFO-grade automation, forecasting models, and reporting
                  that scale cleanly and stand up to diligence and audit scrutiny.
                </ExecutionCardText>
              </ExecutionCard>

              <ExecutionCard>
                <ExecutionCardTitle>AI-enabled operational uplift</ExecutionCardTitle>
                <ExecutionCardText>
                  Practical automation to reduce manual effort,
                  improve decision cadence, and increase operational leverage —
                  without “AI theatre.”
                </ExecutionCardText>
              </ExecutionCard>

              <ExecutionCard>
                <ExecutionCardTitle>Execution, governed properly</ExecutionCardTitle>
                <ExecutionCardText>
                  Clear scope, accountable delivery, and audit-grade controls —
                  aligned to transaction milestones or strategic objectives.
                </ExecutionCardText>
              </ExecutionCard>
            </ExecutionRight>
          </ExecutionBridge>

          {/* OPTIII BRAND STRIP */}
          <OptiiiBrandStrip>
            <OptiiiBrandLeft>
              <OptiiiLogo>Optiii</OptiiiLogo>
              <OptiiiTagline>
                Specialists in finance uplift & automation
              </OptiiiTagline>
            </OptiiiBrandLeft>

            <OptiiiBrandRight>
              <OptiiiCTA href="https://optiii.com" target="_blank" rel="noopener noreferrer">
                Explore Optiii capabilities →
              </OptiiiCTA>
            </OptiiiBrandRight>
          </OptiiiBrandStrip>
        </InnerSection>
      </AltSection>



      {/* ===================================================
         TEAM / LEADERSHIP (Dark)
      =================================================== */}
    
        <LeadershipSection id="team" ref={registerDarkSection}>
        <InnerSection>
          <LeadershipInner>
          

            {/* Content */}
            <LeaderContent
              as={motion.div}
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              viewport={{ once: true }}
            >
              <LeaderEyebrow>Team</LeaderEyebrow>

              <LeaderName>Senior-led, team-delivered</LeaderName>
              <LeaderRole>Principal-led advisory supported by dedicated delivery capability</LeaderRole>

              <LeaderBio>
                SME Cap Advisory is designed to feel like a deal team — not a solo operator. You get senior judgement and negotiation leadership,
                backed by structured delivery across valuation, research, materials, and diligence coordination.
                <br /><br />
                Our approach is grounded in real finance and transaction environments: board-level reporting discipline, capital markets rigour,
                and the operational reality of running an SME during a transaction.
              </LeaderBio>

              
            </LeaderContent>

                    <LeaderImageWrap
              as={motion.div}
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              viewport={{ once: true }}
            >
              <TeamGrid>
                {TEAM_BLOCKS.map((b) => {
                  const Icon = b.icon;
                  return (
                    <TeamCard key={b.title}>
                      <TeamIcon><Icon size={18} /></TeamIcon>
                      <div>
                        <TeamTitle>{b.title}</TeamTitle>
                        <TeamText>{b.text}</TeamText>
                      </div>
                    </TeamCard>
                  );
                })}
              </TeamGrid>

              <TeamNote>
                We collaborate with your legal, tax, and accounting advisors — keeping responsibilities clear and ensuring the transaction stays controlled.
              </TeamNote>
              
            </LeaderImageWrap>

          </LeadershipInner>
              </InnerSection>
        </LeadershipSection>
       

      {/* ===================================================
         INDUSTRIES
      =================================================== */}
     <AltSection>
        <InnerSection>
          <Header>
            <SectionTitle>Industries we advise</SectionTitle>
            <SectionSub>
              Transaction outcomes depend on industry nuance.
              Buyers price risk differently across sectors — we tailor strategy,
              positioning, and process accordingly.
            </SectionSub>
          </Header>

          <IndustryShell>
            {/* LEFT – selector */}
            <IndustryTabs>
              {INDUSTRY_GROUPS.map((g) => (
                <IndustryTab
                  key={g.key}
                  $active={g.key === selectedIndustry}
                  onClick={() => setSelectedIndustry(g.key)}
                >
                  {g.title}
                </IndustryTab>
              ))}
            </IndustryTabs>

            {/* RIGHT – detail */}
            <IndustryDetail
              as={motion.div}
              key={selectedIndustry}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            >
              {INDUSTRY_GROUPS.filter(i => i.key === selectedIndustry).map(i => (
                <div key={i.key}>
                  <IndustryTitle>{i.title}</IndustryTitle>
                  <IndustryLens>
                    <strong>Buyer lens:</strong> {i.buyerLens}
                  </IndustryLens>

                  <IndustryPoints>
                    {i.points.map(p => (
                      <li key={p}><Check size={16} /> {p}</li>
                    ))}
                  </IndustryPoints>
                </div>
              ))}
            </IndustryDetail>
          </IndustryShell>

          {/* Breadth still matters */}
          <IndustryFootnote>
            We also advise across manufacturing, wholesale & distribution,
            logistics, education, agribusiness, and media —
            applying the same buyer-led discipline to each engagement.
          </IndustryFootnote>
        </InnerSection>
      </AltSection>

      {/* ===================================================
         HOW WE WORK
      =================================================== */}
      <Section>
        <InnerSection>
          <SectionTitle>How we work with you</SectionTitle>
          <SectionSub>
            For many business owners, a transaction is a once-in-a-lifetime event. Our role is to bring structure, momentum, and calm to a complex process —
            so decisions are made deliberately, not reactively.
          </SectionSub>

          <Grid>
            {ITEMS.map((item, i) => (
              <Card
                key={item.title}
                as={motion.div}
                custom={i}
                variants={{
                  hidden: { opacity: 0, y: 24 },
                  visible: (idx) => ({
                    opacity: 1,
                    y: 0,
                    transition: { delay: idx * 0.12, duration: 0.6, ease: [0.22, 1, 0.36, 1] },
                  }),
                }}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-120px' }}
              >
                <SmallBox>{i + 1}</SmallBox>
                <CardInnerNext>
                  <CardTitle>{item.title}</CardTitle>
                  <CardText>{item.text}</CardText>
                </CardInnerNext>
              </Card>
            ))}
          </Grid>
        </InnerSection>
      </Section>

     

      {/* ===================================================
         CTA (Dark) — also used for navbar tone switching
      =================================================== */}
      <CTA id="contact" ref={registerDarkSection}>
        <Inner>
          {/* LEFT CONTENT */}
          <Left
            as={motion.div}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Title>Ready to make your next strategic move?</Title>

            <Copy>
              Partner with SME Cap Advisory to navigate business sales, acquisitions, and corporate strategy with clarity and confidence.
              We support you through valuation preparation, transaction planning, negotiation support, and execution coordination — ensuring decisions are commercially sound
              and outcomes are maximised.
            </Copy>

            <CTAHighlights>
              <CTAHighlight><Check size={18} /> Confidential, senior-led engagements</CTAHighlight>
              <CTAHighlight><Check size={18} /> Buyer-first positioning and materials</CTAHighlight>
              <CTAHighlight><Check size={18} /> Disciplined process control through to completion</CTAHighlight>
            </CTAHighlights>

            <EmailLink href="mailto:team@smecapadvisory.com">
              team@smecapadvisory.com <ArrowUpRight size={18} />
            </EmailLink>
          </Left>

          {/* RIGHT FORM */}
              <FormCard
                as={motion.div}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.08 }}
                viewport={{ once: true }}
              >
                {!state.succeeded ? (
                  <>
                    <FormTitle>
                      Start a no-commitment discussion about realising value from your business
                    </FormTitle>

                    <form onSubmit={onSubmit}>
                      <input type="hidden" name="_subject" value="SME Cap Advisory" />
                      <input type="text" name="_gotcha" style={{ display: 'none' }} />


                      <Field>
                        <label>Your name</label>
                        <input
                          type="text"
                          name="name"
                          required
                          placeholder="Your full name"
                          onChange={(e) =>
                            setSubmittedData(prev => ({ ...prev, name: e.target.value }))
                          }
                        />
                        <ValidationError prefix="Name" field="name" errors={state.errors} />
                      </Field>

                      <Field>
                        <label>Email</label>
                        <input
                          type="email"
                          name="email"
                          required
                          placeholder="you@company.com"
                          onChange={(e) =>
                            setSubmittedData(prev => ({ ...prev, email: e.target.value }))
                          }
                        />
                        <ValidationError prefix="Email" field="email" errors={state.errors} />
                      </Field>

                      <Field>
                        <label>Phone (optional)</label>
                        <input
                          type="tel"
                          name="phone"
                          required={false}
                          placeholder="+61 423 543 323"
                          onChange={(e) =>
                            setSubmittedData(prev => ({ ...prev, phone: e.target.value }))
                          }
                        />
                        <ValidationError prefix="Phone" field="phone" errors={state.errors} />
                      </Field>

                      <Field>
                        <label>Company</label>
                        <input
                          type="text"
                          name="company"
                          placeholder="Your Company Pty Ltd"
                          onChange={(e) =>
                            setSubmittedData(prev => ({ ...prev, company: e.target.value }))
                          }
                        />
                        <ValidationError prefix="Company" field="company" errors={state.errors} />
                      </Field>

                      <Field>
                        <label>How can we help?</label>
                        <textarea
                          name="message"
                          rows={4}
                          placeholder="Understanding the value of my business, selling a business, ..."
                        />
                        <ValidationError prefix="Message" field="message" errors={state.errors} />
                      </Field>

                      <SubmitButton
                          type="submit"
                          disabled={state.submitting}
                        >
                          {state.submitting ? 'Sending…' : `I'm interested to understand more`}
                        </SubmitButton>
                      </form>
                    </>
                  ) : (
                    <SuccessWrap
                      as={motion.div}
                      initial={{ opacity: 0, scale: 0.98 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5, ease: 'easeOut' }}
                    >
                      <SuccessTitle>
                        Thanks{submittedData.name ? `, ${submittedData.name}` : ''}.
                      </SuccessTitle>

                      <SuccessText>
                        We'll be in touch shortly to arrange a <strong>confidential, obligation-free</strong> discussion on how SME Cap Advisory can help{' '}
                        <strong>
                          {submittedData.company || 'your business'}
                        </strong>{' '}
                        understand and realise its true value
                      </SuccessText>

                      <SuccessSub>
                        We typically respond within <strong>24 hours</strong>.
                      </SuccessSub>
                    </SuccessWrap>
                  )}
                </FormCard>

        </Inner>
      </CTA>

     

      {/* ===================================================
         FOOTER
      =================================================== */}
      <FooterWrap>
        <TopDivider />

        <Inner>
          {/* LEFT */}
          <Brand>
            <LogoWrap onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <Logo src={assets.LogoWhite} alt="SME Cap Advisory" />
            </LogoWrap>
            <BrandText>
              Boutique corporate advisory for Australian SME business sales valuations and capital strategy —
              delivered through senior-led, disciplined execution.
            </BrandText>
             <Footnote>
              SME Cap Advisory does not sell specific subscriptions or software. All work is delivered through senior-led consulting engagements.
              <br /><br />
              SME Cap Advisory provides management consulting and implementation services only.
              SME Cap Advisory does not provide financial product advice, deal in financial products,
              or hold an Australian Financial Services Licence (AFSL). 
              <br /><br />
              Any share sale, equity transaction, or financial product advice is provided solely by appropriately licensed third-party advisers.
            </Footnote>
          </Brand>

          {/* RIGHT */}
          <Links>
            <Column>
              <ColumnTitle>Services</ColumnTitle>
              <a href="#services" onClick={(e) => (e.preventDefault(), scrollToId('services'))}>Selling a business</a>
              <a href="#services" onClick={(e) => (e.preventDefault(), scrollToId('services'))}>Business valuations</a>
              <a href="#services" onClick={(e) => (e.preventDefault(), scrollToId('services'))}>Business sale advisory</a>
              <a href="#services" onClick={(e) => (e.preventDefault(), scrollToId('services'))}>Capital strategy</a>
            </Column>

            <Column>
              <ColumnTitle>Company</ColumnTitle>
              <a href="#advantage" onClick={(e) => (e.preventDefault(), scrollToId('advantage'))}>Advantage</a>
              <a href="#process" onClick={(e) => (e.preventDefault(), scrollToId('process'))}>Process</a>
              <a href="#team" onClick={(e) => (e.preventDefault(), scrollToId('team'))}>Team</a>
              <a href="#contact" onClick={(e) => (e.preventDefault(), scrollToId('contact'))}>Contact</a>
              <a href="/privacy">Privacy policy</a>
              <a href="/terms">Terms of service</a>
            </Column>
          </Links>
        </Inner>

        <BottomDivider />

        <BottomRow>
          <Copy>© {currentYear} SME Cap Advisory. All rights reserved.</Copy>
          <Location>Based in Australia • Serving businesses nationally</Location>
        </BottomRow>
      </FooterWrap>

      {/* ===================================================
         BACK TO TOP
      =================================================== */}
      <TopButton
        as={motion.button}
        style={{ opacity: showTop }}
        whileHover={{ scale: 1.1 }}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Back to top"
      >
        <ArrowUp />
      </TopButton>
    </Page>
  );
};

export default HomePage;

/* =========================================================
   STYLES
========================================================= */

const Page = styled.div`
  background: #e8eaed;
`;


const SuccessWrap = styled.div`
  min-height: 360px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 20px;
`;

const SuccessTitle = styled.h3`
  font-size: 28px;
  font-weight: 500;
  color: ${({ theme }) => theme.text};
`;

const SuccessText = styled.p`
  font-size: 18px;
  line-height: 1.6;
  color: ${({ theme }) => theme.subText};

  strong {
    color: ${({ theme }) => theme.text};
    font-weight: 500;
  }
`;

const SuccessSub = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.subText};
`;


/* --- Nav --- */
const Nav = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 80;
  transition: all 0.25s ease;

  /* Tone switching */
  backdrop-filter: blur(14px);
`;

const NavInner = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
`;

const BrandRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 999px;

  &:hover {
    background: rgba(255,255,255,0.06);
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;

  @media (max-width: 860px) {
    display: none;
  }
`;

const NavLink = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 14px;
  padding: 8px 10px;
  border-radius: 999px;
  transition: all 0.2s ease;

  color: ${({ $tone }) => ($tone === 'dark' ? 'rgba(255,255,255,0.86)' : 'rgba(0,0,0,0.78)')};
  background: ${({ $tone }) => ($tone === 'dark' ? 'rgba(255,255,255,0)' : 'rgba(0,0,0,0)')};

  &:hover {
    color: ${({ $tone }) => ($tone === 'dark' ? '#fff' : '#000')};
    background: ${({ $tone }) => ($tone === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)')};
  }
`;


const ExecutionBridge = styled.div`
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  gap: 28px;
  margin-top: 28px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ExecutionLeft = styled.div`
  background: rgba(0,0,0,0.035);
  border: 1px solid rgba(0,0,0,0.06);
  border-radius: 24px;
  padding: 26px;
`;

const ExecutionTitle = styled.div`
  font-weight: 850;
  margin-bottom: 10px;
`;

const ExecutionText = styled.p`
  color: rgba(0,0,0,0.72);
  line-height: 1.7;
`;

const ExecutionCallout = styled.div`
  margin-top: 16px;
  background: rgba(0,0,0,0.06);
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 16px;
  padding: 14px;
  font-size: 14px;
  line-height: 1.6;
`;

const ExecutionRight = styled.div`
  display: grid;
  gap: 14px;
`;

const ExecutionCard = styled.div`
  background: #ffffff;
  border: 1px solid rgba(0,0,0,0.06);
  border-radius: 22px;
  padding: 20px;
`;

const ExecutionCardTitle = styled.div`
  font-weight: 800;
  margin-bottom: 6px;
`;

const ExecutionCardText = styled.div`
  color: rgba(0,0,0,0.70);
  line-height: 1.6;
  font-size: 14px;
`;

const OptiiiBrandStrip = styled.div`
  margin-top: 32px;
  padding: 22px 26px;
  border-radius: 24px;
  background: linear-gradient(135deg, #0b0f19, #141a2e);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;

  @media (max-width: 700px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const OptiiiBrandLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const OptiiiLogo = styled.div`
  font-size: 26px;
  font-weight: 700;
  letter-spacing: 0.2px;
`;

const OptiiiTagline = styled.div`
  font-size: 14px;
  color: rgba(255,255,255,0.75);
`;

const OptiiiBrandRight = styled.div``;

const OptiiiCTA = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 18px;
  border-radius: 999px;
  background: linear-gradient(135deg, #7c5cff, #9b7bff);
  color: #ffffff;
  font-weight: 600;
  text-decoration: none;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-1px);
  }
`;

const NavCTA = styled.button`
  border: none;
  cursor: pointer;
  border-radius: 999px;
  padding: 10px 14px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-weight: 700;
  transition: all 0.2s ease;

  background: ${({ $tone }) => ($tone === 'dark' ? '#fff' : '#000')};
  color: ${({ $tone }) => ($tone === 'dark' ? '#000' : '#fff')};

  &:hover {
    transform: translateY(-1px);
  }

  @media (max-width: 860px) {
    padding: 10px 12px;
    font-size: 14px;
  }
`;

const BuyerMemoEyebrow = styled.div`
  text-transform: uppercase;
  font-size: 12px;
  letter-spacing: 1.3px;
  color: rgba(0,0,0,0.55);
  margin-bottom: 8px;
`;

/* --- Hero --- */
const Hero = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 0px 32px;
  overflow: hidden;
`;

const HeroVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(rgba(255,255,255,0.7), rgba(255,255,255,0.7));
  z-index: 1;
`;

const HeroInner = styled.div`
  max-width: 1100px;
  padding: 110px 0px 60px;
  z-index: 2;
`;

const HeroKicker = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 1.1px;
  color: rgba(0,0,0,0.72);
  margin-bottom: 18px;
`;

const HeroTitle = styled.h1`
  font-size: clamp(44px, 7vw, 76px);
  font-weight: 300;
  line-height: 1.05;

  em {
    font-style: italic;
    font-weight: 400;
  }
`;

const HeroSub = styled.p`
  font-size: 30px;
  margin: 24px 0 34px;
  font-family: newFontFaceLight;

  @media (max-width: 599px) {
    font-size: 16px;
  }
`;

const HeroActions = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const IndustryShell = styled.div`
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  gap: 28px;
  width: 100%;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const IndustryTabs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const IndustryTab = styled.button`
  text-align: left;
  padding: 14px 16px;
  border-radius: 18px;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(0,0,0,0.18)' : 'rgba(0,0,0,0.08)')};
  background: ${({ $active }) => ($active ? 'rgba(0,0,0,0.06)' : '#fff')};
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: rgba(0,0,0,0.08);
  }
`;

const IndustryDetail = styled.div`
  background: rgba(0, 0, 0, 0.06);
  border-radius: 24px;
  border: 1px solid rgba(0,0,0,0.08);
  padding: 26px;
`;

const IndustryTitle = styled.div`
  font-size: 22px;
  font-weight: 800;
  margin-bottom: 8px;
`;

const IndustryLens = styled.div`
  font-size: 14px;
  color: rgba(0,0,0,0.7);
  margin-bottom: 16px;
`;

const IndustryPoints = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    margin-bottom: 10px;
    color: rgba(0,0,0,0.8);
    line-height: 1.6;
  }
`;

const IndustryFootnote = styled.p`
  margin-top: 32px;
  font-size: 14px;
  color: rgba(0,0,0,0.6);
  max-width: 1100px;
`;


const PrimaryCTA = styled.button`
  background: #000;
  color: #fff;
  padding: 16px 26px;
  border-radius: 999px;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.25s;
  transform: scale(1);

  &:hover { transform: scale(1.02); }
  &:active { transform: scale(0.98); }
`;

const SecondaryCTA = styled.button`
  background: #fff;
  border: 1px solid rgba(0,0,0,0.14);
  padding: 16px 26px;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.25s;
  transform: scale(1);

  &:hover { transform: scale(1.02); }
  &:active { transform: scale(0.98); }
`;


const BuyerValueSection = styled.div`
  margin-top: 48px;
  width: 100%;
`;

const BuyerValueGrid = styled.div`
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  gap: 18px;
  width: 100%;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const BuyerValueLeft = styled.div`
  background: rgba(0,0,0,0.035);
  border: 1px solid rgba(0,0,0,0.05);
  border-radius: 24px;
  padding: 26px;
`;

const BuyerValueTitle = styled.div`
  font-weight: 850;
  margin-bottom: 10px;
`;

const BuyerValueText = styled.p`
  color: rgba(0,0,0,0.72);
  line-height: 1.7;
  margin-bottom: 14px;
`;

const BuyerValueCallout = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: rgba(0,0,0,0.8);
  background: rgba(255,255,255,0.7);
  border-radius: 14px;
  padding: 12px 14px;
  border: 1px solid rgba(0,0,0,0.08);
`;

const BuyerValueRight = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const BuyerValueCard = styled.div`
  background: #fff;
  border: 1px solid rgba(0,0,0,0.06);
  border-radius: 22px;
  padding: 18px;
`;

const BuyerPath = styled.div`
  display: grid;
  gap: 26px;
`;

const BuyerStep = styled.div`
  display: grid;
  grid-template-columns: 48px 1fr;
  gap: 18px;
  align-items: flex-start;
`;

const BuyerStepIndex = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 999px;
  background: #000;
  color: #fff;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BuyerStepContent = styled.div`
  background: rgba(255,255,255,0.75);
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 22px;
  padding: 22px;
`;

const BuyerStepTitle = styled.div`
  font-weight: 850;
  margin-bottom: 8px;
`;

const BuyerStepText = styled.p`
  color: rgba(0,0,0,0.72);
  line-height: 1.7;
  margin: 0;
`;

const BuyerCriteriaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0,1fr));
  gap: 14px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const BuyerCriteria = styled.div`
  background: rgba(0,0,0,0.04);
  border: 1px solid rgba(0,0,0,0.06);
  border-radius: 16px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  strong {
    font-weight: 800;
  }

  span {
    color: rgba(0,0,0,0.7);
    font-size: 14px;
    line-height: 1.55;
  }
`;

const BuyerSplit = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0,1fr));
  gap: 18px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const BuyerSplitCol = styled.div`
  ul {
    margin: 8px 0 0;
    padding-left: 18px;
    color: rgba(0,0,0,0.72);
    line-height: 1.6;
  }
`;

const BuyerSplitTitle = styled.div`
  font-weight: 800;
`;

const BuyerStepCallout = styled.div`
  margin-top: 14px;
  background: rgba(0,0,0,0.05);
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 14px;
  padding: 12px 14px;
  font-size: 14px;
`;


const BuyerValueCardTitle = styled.div`
  font-weight: 800;
  margin-bottom: 6px;
`;

const BuyerValueCardText = styled.div`
  color: rgba(0,0,0,0.7);
  line-height: 1.65;
`;

const BuyerValueStrip = styled.div`
  margin-top: 18px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const BuyerStripItem = styled.div`
  background: rgba(255,255,255,0.65);
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 18px;
  padding: 14px 16px;
`;

const BuyerStripTitle = styled.div`
  font-weight: 700;
  margin-bottom: 6px;
`;

const BuyerStripText = styled.div`
  color: rgba(0,0,0,0.70);
  line-height: 1.55;
  font-size: 14px;
`;


const PillAlign = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: 10px;
  width: 100%;
  margin-top: 34px;
  flex-wrap: wrap;
`;

const Pill = styled.div`
  color: #000;
  border-radius: 999px;
  font-size: 13px;
  display: flex;
  justify-content: flex-start;
  gap: 10px;
  align-items: center;
  width: 220px;

  @media (max-width: 599px) {
    width: 100%;
  }
`;

const PillDot = styled.div`
  background: #000;
  height: 5px;
  width: 5px;
  border-radius: 20px;
  flex-grow: 0;
  flex-shrink: 0;
`;

const CredStrip = styled.div`
  margin-top: 26px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const CredItem = styled.div`
  background: rgba(255,255,255,0.65);
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 18px;
  padding: 14px 16px;
`;

const CredTitle = styled.div`
  font-weight: 700;
  margin-bottom: 6px;
`;

const CredText = styled.div`
  color: rgba(0,0,0,0.70);
  line-height: 1.55;
  font-size: 14px;
`;

const ScrollCue = styled.div`
  position: absolute;
  bottom: 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #000;
  font-size: 13px;
  letter-spacing: 0.3px;
  z-index: 3;
`;

const ScrollCueInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #000;
  font-size: 13px;
  letter-spacing: 0.3px;
`;

/* --- Sections --- */
const Section = styled.section`
  padding: 50px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const AltSection = styled(Section)`
  background: #fff;
  padding: 50px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const InnerSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  position: relative;
  width: 100%;
  max-width: 1100px;
  padding-top: 100px;
  padding-bottom: 100px;
`;

const Eyebrow = styled.div`
  text-transform: uppercase;
  font-size: 13px;
  letter-spacing: 1.2px;
  color: #777;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h2`
  font-size: 38px;
  font-weight: 400;
  margin-bottom: 20px;
`;

const SectionSub = styled.p`
  color: #666;
  margin-bottom: 60px;
  width: 100%;
`;

const Body = styled.p`
  font-size: 20px;
  max-width: 900px;
  color: #222;
  margin-bottom: 30px;
`;

const Header = styled.div`
  margin-bottom: 32px;
  width: 100%;
`;

/* --- Cards/Grids --- */
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 40px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 22px;
  cursor: pointer;
  position: relative;
  transition: background 0.3s ease;
  gap: 15px;

  &:hover {
    background: #f5f5f5;
  }
`;

const CardNext = styled.div`
  background: #f5f5f5;
  border-radius: 22px;
  cursor: pointer;
  position: relative;
  transition: background 0.3s ease;
  gap: 15px;

  &:hover {
    background: rgba(0,0,0,0.08);
  }
`;

const CardInner = styled.div`
  padding: 25px;
  gap: 20px;
  display: flex;
  flex-direction: column;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const CardTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  line-height: 1.3;
  max-width: 90%;
  margin: 0;
`;

const CardDescription = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: #4b5563;
  width: 100%;
  margin: 0;
`;

const ArrowWrap = styled(motion.div)`
  color: #9ca3af;
`;

const ServiceNote = styled.div`
  margin-top: 22px;
  padding: 16px 18px;
  border-radius: 18px;
  background: rgba(0,0,0,0.04);
  border: 1px solid rgba(0,0,0,0.06);
  color: rgba(0,0,0,0.72);
  line-height: 1.6;
`;

/* --- Trust grid --- */
const TrustGrid = styled.div`
  margin-top: 24px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const TrustCard = styled.div`
  background: rgba(0,0,0,0.04);
  border: 1px solid rgba(0,0,0,0.06);
  border-radius: 22px;
  padding: 18px;
`;

const TrustTop = styled.div`
  display: flex;
  gap: 14px;
  align-items: flex-start;
`;

const TrustIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: #000;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const TrustTitle = styled.div`
  font-weight: 800;
  margin-bottom: 6px;
`;

const TrustText = styled.div`
  color: rgba(0,0,0,0.70);
  line-height: 1.6;
`;

/* --- Stats --- */
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(200px, 1fr));
  gap: 10px;

  @media (max-width: 899px) {
    grid-template-columns: repeat(2, minmax(200px, 1fr));
  }
`;

const Stat = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 56px;
  font-weight: 500;
`;

const StatValueSmaller = styled.div`
  font-size: 36px;
  font-weight: 500;
  height: 85px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StatLabel = styled.div`
  color: #000;
  font-size: 14px;
`;

const StatLabelSmall = styled.div`
  color: #666;
  font-size: 12px;
`;

const ValueNarrative = styled.div`
  margin-top: 26px;
  width: 100%;
  background: rgba(255,255,255,0.75);
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 22px;
  padding: 20px 22px;
`;

const ValueNarrativeTitle = styled.div`
  font-weight: 850;
  margin-bottom: 8px;
`;

const ValueNarrativeText = styled.div`
  color: rgba(0,0,0,0.72);
  line-height: 1.7;
`;

const ValueBullets = styled.ul`
  margin: 14px 0 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    color: rgba(0,0,0,0.80);
    margin-bottom: 10px;

    svg { margin-top: 2px; }
  }
`;

/* --- Advantage cards --- */
const Cards = styled.div`
  display: grid;
  gap: 28px;
  width: 100%;
`;

const CardOther = styled.div`
  background: #f5f5f5;
  border-radius: 22px;
  cursor: pointer;
  position: relative;
  padding: 22px;
  gap: 20px;
  display: flex;
  flex-direction: column;
  width: 100%;
  border: 1px solid rgba(0,0,0,0.0);
  transition: background 0.25s ease, box-shadow 0.25s ease;
  will-change: transform;


  &:hover {
    box-shadow: 0 10px 24px rgba(0,0,0,0.08);
    background: rgba(0,0,0,0.08);
  }
`;

const Top = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const LeftAlign = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 20px;
`;

const IconWrap = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
  flex-shrink: 0;
`;

const StatWrap = styled.div`
  text-align: right;
`;

const StatNew = styled.div`
  font-size: 30px;
  font-family: newFontFace;
  font-weight: 700;
`;

const StatLabelNew = styled.div`
  color: #666;
  font-size: 14px;
`;

const Checklist = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    font-size: 17px;
    color: #111827;
    margin-bottom: 14px;

    svg {
      flex-shrink: 0;
      margin-top: 3px;
    }
  }
`;

const BuyerLens = styled.div`
  margin-top: 22px;
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  gap: 14px;
  width: 100%;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const BuyerLensLeft = styled.div`
  background: rgba(0,0,0,0.04);
  border: 1px solid rgba(0,0,0,0.06);
  border-radius: 22px;
  padding: 18px;
`;

const BuyerLensTitle = styled.div`
  font-weight: 850;
  margin-bottom: 8px;
`;

const BuyerLensText = styled.div`
  color: rgba(0,0,0,0.70);
  line-height: 1.7;
`;

const BuyerLensGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const BuyerLensCard = styled.div`
  background: rgba(255,255,255,0.75);
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 22px;
  padding: 16px;
`;

const BuyerLensCardTitle = styled.div`
  font-weight: 800;
  margin-bottom: 8px;
`;

const BuyerLensCardText = styled.div`
  color: rgba(0,0,0,0.70);
  line-height: 1.65;
`;

/* --- Compact Process --- */
const ProcessShell = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 0.95fr 1.05fr;
  gap: 14px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ProcessSidebar = styled.div`
  background: #fff;
  border-radius: 22px;
  border: 1px solid rgba(0,0,0,0.08);
  padding: 18px;
  box-shadow: 0 18px 44px rgba(0,0,0,0.06);
`;

const ProcessMeta = styled.div`
  padding: 8px 8px 16px;
  border-bottom: 1px solid rgba(0,0,0,0.08);
  margin-bottom: 12px;
`;

const ProcessMetaLabel = styled.div`
  color: rgba(0,0,0,0.58);
  font-size: 13px;
  margin-bottom: 6px;
`;

const ProcessMetaValue = styled.div`
  font-size: 34px;
  font-weight: 700;
`;

const ProcessMetaSub = styled.div`
  margin-top: 6px;
  color: rgba(0,0,0,0.58);
  font-size: 13px;
  line-height: 1.5;
`;

const ProcessSteps = styled.div`
  display: grid;
  gap: 10px;
  margin-top: 12px;
`;

const ProcessStepButton = styled.button`
  width: 100%;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(0,0,0,0.22)' : 'rgba(0,0,0,0.10)')};
  background: ${({ $active }) => ($active ? 'rgba(0,0,0,0.06)' : 'rgba(0,0,0,0.03)')};
  border-radius: 18px;
  padding: 12px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ $active }) => ($active ? 'rgba(0,0,0,0.08)' : 'rgba(0,0,0,0.06)')};
  }
`;

const ProcessStepLeft = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  text-align: left;
`;

const ProcessStepIdx = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 999px;
  background: #000;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  flex-shrink: 0;
`;

const ProcessStepTitle = styled.div`
  font-weight: 850;
  line-height: 1.25;
`;

const ProcessStepMini = styled.div`
  color: rgba(0,0,0,0.58);
  font-size: 12px;
  margin-top: 2px;
`;

const ProcessHint = styled.div`
  margin-top: 12px;
  color: rgba(0,0,0,0.55);
  font-size: 12px;
  line-height: 1.6;
`;

const ProcessDetail = styled.div`
  background: #fff;
  border-radius: 22px;
  border: 1px solid rgba(0,0,0,0.08);
  padding: 22px;
  box-shadow: 0 18px 44px rgba(0,0,0,0.06);
`;

const ProcessDetailTop = styled.div`
  margin-bottom: 14px;
`;

const ProcessDetailEyebrow = styled.div`
  color: rgba(0,0,0,0.58);
  font-size: 13px;
  margin-bottom: 8px;
`;

const ProcessDetailTitle = styled.div`
  font-size: 24px;
  font-weight: 850;
  margin-bottom: 10px;
`;

const ProcessDetailText = styled.div`
  color: rgba(0,0,0,0.70);
  line-height: 1.7;
`;

const KeyLabel = styled.div`
  font-weight: 800;
  margin: 16px 0 12px;
`;

const ProcessAssurance = styled.div`
  margin-top: 16px;
  display: grid;
  gap: 10px;
`;

const AssuranceItem = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  background: rgba(0,0,0,0.03);
  border: 1px solid rgba(0,0,0,0.06);
  border-radius: 18px;
  padding: 12px 12px;
`;

const AssuranceIcon = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 12px;
  background: #000;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const AssuranceTitle = styled.div`
  font-weight: 850;
  margin-bottom: 4px;
`;

const AssuranceText = styled.div`
  color: rgba(0,0,0,0.65);
  line-height: 1.55;
  font-size: 13px;
`;

/* --- Quote section (dark overlay) --- */
const SectionOther = styled.section`
  position: relative;
  height: 420px;
  width: 100%;
  overflow: hidden;

  @media (max-width: 768px) {
    height: 360px;
  }
`;

const Background = styled.div`
  position: absolute;
  inset: 0;
  background-image: url('https://images.unsplash.com/photo-1600880292203-757bb62b4baf');
  background-size: cover;
  background-position: center;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
`;

const Content = styled.div`
  position: relative;
  z-index: 2;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 32px;
`;

const Quote = styled.blockquote`
  max-width: 900px;
  color: #ffffff;
  text-align: center;
  font-size: clamp(22px, 3vw, 36px);
  font-weight: 300;
  line-height: 1.35;
  letter-spacing: 0.2px;

`;

/* --- Leadership (dark) --- */
const LeadershipSection = styled.section`
  position: relative;
  padding: 140px 32px;
  background:
    linear-gradient(rgba(0,0,0,0.65), rgba(0,0,0,0.65)),
    url('https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=1600&q=80')
      center / cover no-repeat;

  display:flex;
  justify-content:center;
  align-items:center;
  align-content:center;
`;

const LeadershipInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1.1fr;
  gap: 80px;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 60px;
  }
`;

const LeaderImageWrap = styled.div`
  position: relative;
  border-radius: 22px;
  overflow: hidden;
  box-shadow: 0 40px 80px rgba(0,0,0,0.35);
`;

const LeaderImage = styled.div`
  width: 100%;
  height: 520px;
  background-size: cover;
  background-position: center;

  @media (max-width: 900px) {
    height: 420px;
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(255,255,255,0.45), rgba(255,255,255,0));
`;

const LeaderContent = styled.div`
  color: #fff;
`;

const LeaderEyebrow = styled.div`
  text-transform: uppercase;
  font-size: 13px;
  letter-spacing: 1.4px;
  color: rgba(255,255,255,0.6);
  margin-bottom: 16px;
`;

const LeaderName = styled.h2`
  font-size: clamp(36px, 4vw, 48px);
  font-weight: 400;
  margin: 0 0 8px;
`;

const LeaderRole = styled.div`
  font-size: 20px;
  color: rgba(255,255,255,0.75);
  margin-bottom: 22px;
`;

const LeaderBio = styled.p`
  font-size: 17px;
  line-height: 1.75;
  color: rgba(255,255,255,0.85);
  max-width: 640px;
`;

const TeamGrid = styled.div`
  margin-top: 18px;
  display: grid;
  gap: 12px;
  max-width: 680px;
`;

const TeamCard = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  background: rgba(255,255,255,0.12);
  border: 1px solid rgba(255,255,255,0.16);
  border-radius: 18px;
  padding: 12px 12px;
`;

const TeamIcon = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 12px;
  background: #fff;
  color: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const TeamTitle = styled.div`
  font-weight: 850;
  margin-bottom: 4px;
  color:#ffffff;
`;

const TeamText = styled.div`
  color: rgba(255,255,255,0.80);
  line-height: 1.55;
  font-size: 14px;
`;

const TeamNote = styled.div`
  margin-top: 12px;
  color: rgba(255,255,255,0.70);
  font-size: 13px;
  line-height: 1.6;
`;

/* --- Industry pills --- */
const PillGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  max-width: 1200px;
`;

const PillMore = styled.div`
  color: #000;
  border-radius: 999px;
  font-size: 13px;
  display: flex;
  justify-content: flex-start;
  gap: 10px;
  align-items: center;
  background: rgba(0,0,0,0.05);
  padding: 10px;
  border: 1px solid transparent;
  transition: all 0.25s;
  transform: scale(1);

  &:hover {
    transform: scale(1.02);
    border: 1px solid rgba(0,0,0,0.12);
    background: rgba(0,0,0,0.08);
  }
`;

/* --- How we work cards --- */
const SmallBox = styled.div`
  width: 40px;
  height: 40px;
  font-size: 18px;
  color: #fff;
  background: #000;
  border-radius: 999px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 20px;
  left: -20px;
`;

const CardInnerNext = styled.div`
  padding: 25px;
  gap: 14px;
  display: flex;
  flex-direction: column;
  padding-left: 35px;
`;

const CardText = styled.div`
  font-size: 14px;
  line-height: 1.7;
  color: #374151;
  max-width: 900px;
`;

/* --- CTA --- */
const CTA = styled.section`
  background: #000;
  color: #fff;
  padding: 140px 32px;
`;

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 80px;
  align-items: flex-start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 48px;
  }
`;

const Left = styled.div`

`;

const Title = styled.h2`
  font-size: 40px;
  font-weight: 500;
  margin-bottom: 16px;
  margin-top: 0;
`;

const Copy = styled.p`
  font-size: 20px;
  line-height: 1.6;
  color: #cfcfcf;
  margin-bottom: 18px;
  width:100%;
`;

const CTAHighlights = styled.div`
  display: grid;
  gap: 10px;
  margin: 0 0 22px;
  color: rgba(255,255,255,0.85);
`;

const CTAHighlight = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  svg { margin-top: 2px; }
`;

const EmailLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 20px;
  color: #fff;
  text-decoration: none;
  border-bottom: 1px solid rgba(255,255,255,0.4);
  padding-bottom: 4px;
  width: fit-content;

  &:hover {
    border-color: #fff;
  }
`;

const FormCard = styled.div`
  background: linear-gradient(180deg, rgba(255,255,255,0.12), rgba(255,255,255,0.06));
  backdrop-filter: blur(12px);
  border-radius: 28px;
  padding: 40px;
  box-shadow: 0 40px 80px rgba(0,0,0,0.5);

  @media (max-width: 900px) {
    padding: 32px 24px;
  }
`;

const FormTitle = styled.h3`
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 24px;
  margin-top: 0;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 14px;

  label {
    font-size: 13px;
    color: rgba(245,245,247,0.72);
    margin-bottom: 6px;
  }

  input, textarea {
    background: rgba(0,0,0,0.25);
    border: 1px solid rgba(255,255,255,0.16);
    border-radius: 14px;
    padding: 12px 14px;
    color: #fff;
    font-size: 15px;
    line-height: 1.5;

    &::placeholder { color: rgba(245,245,247,0.45); }

    &:focus {
      outline: none;
      border-color: rgba(255,255,255,0.32);
    }
  }

  textarea { resize: vertical; min-height: 110px; }
`;

const SubmitButton = styled.button`
  margin-top: 14px;
  background: #fff;
  color: #000;
  border: none;
  border-radius: 999px;
  padding: 16px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
`;

const Footnote = styled.div`
  margin-top: 14px;
  font-size: 12px;
  color: #a3a3a3;
  text-align: center;
`;

/* --- Footer --- */
const FooterWrap = styled.footer`
  background: #000;
  color: #9ca3af;
  padding: 0 32px 40px;
`;

const TopDivider = styled.div`
  height: 1px;
  background: rgba(255,255,255,0.12);
  margin-bottom: 64px;
`;

const BottomDivider = styled.div`
  height: 1px;
  background: rgba(255,255,255,0.12);
  margin: 72px 0 32px;
`;

const Brand = styled.div`
  max-width: 420px;
`;

const LogoWrap = styled.div`
  cursor: pointer;
`;

const Logo = styled.img`
  height: 40px;
`;

const BrandText = styled.p`
  font-size: 18px;
  line-height: 1.6;
  color: #9ca3af;
`;

const Links = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 80px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 48px;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;

  a {
    color: #9ca3af;
    text-decoration: none;
    font-size: 18px;
    transition: color 0.2s ease;

    &:hover {
      color: #fff;
    }
  }
`;

const ColumnTitle = styled.h4`
  font-size: 20px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 8px;
`;

const BottomRow = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;

  @media (max-width: 700px) {
    flex-direction: column;
    gap: 12px;
    text-align: center;
  }
`;

const Location = styled.div`
  color: #9ca3af;
`;



/* --- Back to top --- */
const TopButton = styled.button`
  position: fixed;
  bottom: 28px;
  right: 28px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: #000;
  color: #fff;
  cursor: pointer;
  z-index: 50;
`;

/* --- Misc --- */
const OptiiiNote = styled.p`
  margin-top: 32px;
  font-size: 14px;
  color: #666;
  max-width: 640px;
`;
